import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AuthContextType, AuthProviderProps, TokenClaims, TokenInfo } from './types';
import { useIsAuthenticated, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { decodeJwt } from 'jose';
import { loginRequest } from 'config';

const defaultContext: AuthContextType = {
  tokenInfo: null,
  setTokenInfo: () => {},
};

/**
 * Context for managing authentication data.
 */
export const AuthContext = createContext<AuthContextType>(defaultContext);

/**
 * Provides a React context provider for authentication, initializing and providing the auth state.
 * @param {AuthProviderProps} props - Props for the AuthProvider component, including children.
 * @returns {JSX.Element} The provider component that wraps its children, providing them with access to the authentication context.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const { accounts, instance } = useMsal();

  const isAuthenticated = useIsAuthenticated();
  const request = {
    account: accounts[0],
    scopes: loginRequest.scopes,
  };
  const { error } = useMsalAuthentication(InteractionType.Redirect, request);

  const applyToken = useCallback(
    (token) => {
      if (!token) return;
      const decoded = decodeJwt(token) as TokenClaims;
      if (!decoded.exp) {
        return;
      }
      setTokenInfo({
        expiration: new Date(decoded.exp * 1000),
        accessToken: token,
        groups: decoded.groups ?? [],
        roles: decoded.roles ?? [],
        authenticationMethod: decoded.mfaMethod,
      });
    },
    [setTokenInfo]
  );

  useEffect(() => {
    if (isAuthenticated && !tokenInfo) {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((r) => applyToken(r?.accessToken))
        .catch((err) => {
          console.log(err);
          instance.logoutRedirect();
        });
    }

    // eslint-disable-next-line
  }, [isAuthenticated]);

  if (error) {
    instance.logoutRedirect();
  }

  return (
    <AuthContext.Provider
      value={{
        tokenInfo: tokenInfo,
        setTokenInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom React hook for accessing the authentication context.
 * @returns {AuthContextType} The current authentication context, providing access and utility functions for managing authentication state.
 */
export const useAuth = () => useContext(AuthContext);
